import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  FileText, 
  Download, 
  Loader2,
  Eye,
  Share2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ExtendedLead } from './types';

interface Proposta {
  id: string;
  lead_id: string;
  proposta_pdf_url: string | null;
  created_at: string;
  updated_at: string;
}

interface PropostasProps {
  leadData: ExtendedLead;
}

export default function Propostas({ leadData }: PropostasProps) {
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchPropostas = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('propostas_clientes')
        .select('id, lead_id, proposta_pdf_url, created_at, updated_at')
        .eq('lead_id', leadData.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filtrar apenas propostas que possuem proposta_pdf_url
      const propostasComPdf = (data || []).filter(p => p.proposta_pdf_url !== null);
      setPropostas(propostasComPdf);
    } catch (error) {
      console.error('Erro ao buscar propostas:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as propostas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (leadData.id) {
      fetchPropostas();
    }
  }, [leadData.id]);

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  const handleShare = (url: string) => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(url)}`;
    window.open(whatsappUrl, '_blank');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFileName = (url: string) => {
    return url.split('/').pop() || 'proposta.pdf';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Propostas do Cliente</h3>
          <p className="text-sm text-muted-foreground">
            Propostas geradas na página Propostas
          </p>
        </div>
      </div>

      {/* Lista de propostas */}
      {propostas.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhuma proposta encontrada</h3>
            <p className="text-muted-foreground text-center max-w-md mb-4">
              Este cliente ainda não possui propostas geradas. Acesse a página de Propostas para criar uma proposta para este cliente.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {propostas.map((proposta) => (
            <Card key={proposta.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="h-5 w-5 text-red-500" />
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-sm whitespace-normal break-words">
                        {proposta.proposta_pdf_url ? getFileName(proposta.proposta_pdf_url) : 'Proposta'}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Proposta Comercial
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Gerada em: {formatDate(proposta.created_at)}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => proposta.proposta_pdf_url && handleDownload(proposta.proposta_pdf_url)}
                    className="flex-1"
                    disabled={!proposta.proposta_pdf_url}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Ver
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => proposta.proposta_pdf_url && handleDownload(proposta.proposta_pdf_url)}
                    disabled={!proposta.proposta_pdf_url}
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => proposta.proposta_pdf_url && handleShare(proposta.proposta_pdf_url)}
                    disabled={!proposta.proposta_pdf_url}
                  >
                    <Share2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
